import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";

export const notes = createApi({
  reducerPath: "notes",
  tagTypes: ["ACCOUNT_NOTES"],

  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}`,
  }),

  endpoints: (builder) => ({
    // ✅ Add Attachment API
    addAttachementDocument: builder.mutation<
      any,
      {
        file: File;
        document_type: string;
        document_category: string;
        accountId: string;
        note: string;
      }
    >({
      query: ({ file, document_type, document_category, accountId, note }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("document_type", document_type);
        formData.append("document_category", document_category);
        formData.append("note", note);

        return {
          url: `/accounts/${accountId}/documents`,
          method: "POST",
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["ACCOUNT_NOTES"],
    }),
    submitAttachementDocument: builder.mutation<
      any,
      {
        accountId: string;
        action: string;
        comments: string;
      }
    >({
      query: ({ accountId, action, comments }) => ({
        url: `/comments/account_documents/${accountId}`,
        method: "POST", // <- CHANGED
        data: {
          action,
          comments,
        },
      }),
      transformResponse: (response: any) => response?.data,
      invalidatesTags: ["ACCOUNT_NOTES"],
    }),

    // ✅ Get Attachments API - Enhanced to properly handle all_data
    getAttachementDocuments: builder.query<any, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `/accounts/${accountId}/documents/additional_documents_list`,
        method: "GET",
      }),
      transformResponse: (response: any) => {
        // Enhanced transform to ensure all_data is properly parsed
        const documents = response?.data || [];
        return documents.map((doc: any) => {
          let parsedAllData: { note?: string; [key: string]: any } = {};
          let noteValue = "";

          // Handle all_data parsing
          if (doc.all_data) {
            if (typeof doc.all_data === "string") {
              try {
                parsedAllData = JSON.parse(doc.all_data);
              } catch (e) {
                // If JSON parsing fails, treat the entire string as a note
                parsedAllData = { note: doc.all_data };
              }
            } else if (typeof doc.all_data === "object") {
              parsedAllData = doc.all_data;
            }
          }

          // Extract note value with priority: all_data.note > direct note > all_data as string
          if (parsedAllData?.note) {
            noteValue = parsedAllData?.note;
          } else if (doc.note) {
            noteValue = doc.note;
          } else if (typeof doc.all_data === "string" && doc.all_data.trim()) {
            // If all_data is a string and not JSON, treat it as the note
            noteValue = doc.all_data;
          }

          return {
            ...doc,
            all_data: parsedAllData,
            note: noteValue,
          };
        });
      },
      providesTags: ["ACCOUNT_NOTES"],
    }),
    getAttachementDocumentUrl: builder.query<
      any,
      { document_id: string; accountId: string }
    >({
      query: ({ document_id, accountId }) => ({
        url: `/accounts/${accountId}/documents/${document_id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data,
    }),
    // ✅ Delete Attachment API
    deleteAttachementDocument: builder.mutation<
      any,
      { document_id: string; accountId: string }
    >({
      query: ({ document_id, accountId }) => ({
        url: `/accounts/${accountId}/documents/${document_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ACCOUNT_NOTES"],
    }),
  }),
});

// ✅ Export auto-generated hooks
export const {
  useAddAttachementDocumentMutation,
  useSubmitAttachementDocumentMutation,
  useGetAttachementDocumentsQuery,
  useDeleteAttachementDocumentMutation,
  useGetAttachementDocumentUrlQuery,
} = notes;
